import { useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { debounce } from "lodash";

import { api_without_cancellation } from "../../../../api/api";

export const useDirectorySearch = (config, { entityScope, entityId } = {}) => {
  const { setValue } = useFormContext();
  const [results, setResults] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const path = (field) => (entityScope && entityId ? `${entityScope}.${entityId}.${field}` : field);

  const fetchData = async (query) => {
    try {
      const res = await api_without_cancellation.get(config.endpoint, {
        params: config.buildParams(query),
      });
      const list = config.pickResults(res.data) ?? [];
      setResults(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("Directory search failed:", err);
      setResults([]);
    }
  };

  const debouncedFetch = useMemo(
    () => debounce(fetchData, config.debounceMs ?? 400),
    [config.endpoint]
  );
  useEffect(() => () => debouncedFetch.cancel(), [debouncedFetch]);

  const onInputChange = (e) => {
    const v = e.target.value;
    setSearchTerm(v);
    if (v.trim().length >= (config.minLength ?? 2)) debouncedFetch(v);
    else setResults([]);
  };

  const applySelection = (recordOrId) => {
    const record =
      typeof recordOrId === "object" ? recordOrId : results.find((r) => r?.id === recordOrId);
    if (!record) return;

    for (const [src, dest, transform] of config.fieldMap) {
      const raw = record?.[src];
      const value = transform ? transform(raw ?? "") : raw ?? "";
      setValue(path(dest), value);
    }
    setResults([]);

    if (config.deriveSearchTerm) {
      setSearchTerm(config.deriveSearchTerm(record));
    }
  };

  const clearSelection = (fieldsToClear) => {
    for (const f of fieldsToClear) setValue(path(f), "");
    setSearchTerm("");
    setResults([]);
  };

  return {
    results,
    searchTerm,
    setSearchTerm,
    onInputChange,
    applySelection,
    clearSelection,
  };
};
